import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit"
import type { Notification as NotificationType } from "../../data/notifications"
import apiClient from "../../api/apiClient"

interface NotificationsState {
  notifications: NotificationType[]
  hasPermission: boolean
  isLoading: boolean
  error: string | null
}

const initialState: NotificationsState = {
  notifications: [],
  hasPermission: false,
  isLoading: false,
  error: null,
}

// Async thunks
export const fetchNotifications = createAsyncThunk(
  "notifications/fetchNotifications",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("/notifications")
      return response.data.notifications
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to fetch notifications")
    }
  },
)

export const markNotificationAsRead = createAsyncThunk(
  "notifications/markAsRead",
  async (notificationId: string, { rejectWithValue }) => {
    try {
      await apiClient.put(`/notifications/${notificationId}/read`)
      return notificationId
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to mark notification as read")
    }
  },
)

export const markAllNotificationsAsRead = createAsyncThunk(
  "notifications/markAllAsRead",
  async (_, { rejectWithValue }) => {
    try {
      await apiClient.put("/notifications/read-all")
      return { success: true }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to mark all notifications as read")
    }
  },
)

// Thunk for requesting notification permission
export const requestPermission = createAsyncThunk("notifications/requestPermission", async (_, { dispatch }) => {
  if (!("Notification" in window)) {
    return false
  }

  if (Notification.permission === "granted") {
    dispatch(setPermission(true))
    return true
  }

  if (Notification.permission !== "denied") {
    const permission = await Notification.requestPermission()
    const granted = permission === "granted"
    dispatch(setPermission(granted))

    // Register for push notifications if permission granted
    if (granted) {
      try {
        await apiClient.post("/notifications/register")
      } catch (error) {
        console.error("Failed to register for push notifications:", error)
      }
    }

    return granted
  }

  return false
})

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    markAsRead: (state, action: PayloadAction<string>) => {
      const notification = state.notifications.find((n) => n.id === action.payload)
      if (notification) {
        notification.read = true
      }
    },
    markAllAsRead: (state) => {
      state.notifications.forEach((notification) => {
        notification.read = true
      })
    },
    setPermission: (state, action: PayloadAction<boolean>) => {
      state.hasPermission = action.payload
    },
    addNotification: (state, action: PayloadAction<NotificationType>) => {
      state.notifications.unshift(action.payload)
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch notifications
      .addCase(fetchNotifications.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchNotifications.fulfilled, (state, action: PayloadAction<NotificationType[]>) => {
        state.notifications = action.payload
        state.isLoading = false
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Mark notification as read
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const notification = state.notifications.find((n) => n.id === action.payload)
        if (notification) {
          notification.read = true
        }
      })
      // Mark all notifications as read
      .addCase(markAllNotificationsAsRead.fulfilled, (state) => {
        state.notifications.forEach((notification) => {
          notification.read = true
        })
      })
  },
})

export const { markAsRead, markAllAsRead, setPermission, addNotification } = notificationsSlice.actions

export default notificationsSlice.reducer
